/**
 * Schema registry — the single map of section/block types to their schemas.
 * The seller editor reads this to render the inspector. The buyer renderer
 * reads this to know which React component to dispatch to (component map
 * lives in the buyer app and is keyed by the same `type` strings).
 *
 * Adding a new section type: write a `<type>.ts` schema file under
 * `sections/`, import it here, add an entry. No other registry edits.
 */
import { themeSchema } from "./theme";
import { animationFields, responsiveLayoutFields } from "./sections/_shared";
import { headerSchema } from "./sections/header";
import { navSchema } from "./sections/nav";
import { announcementBarSchema } from "./sections/announcement-bar";
import { heroSchema } from "./sections/hero";
import { collectionsSchema } from "./sections/collections";
import { categorySectionsSchema } from "./sections/category-sections";
import { categoryTabsSchema } from "./sections/category-tabs";
import { allProductsSchema } from "./sections/all-products";
import { footerSchema } from "./sections/footer";
import { bannerSchema } from "./sections/banner";
import { aboutSchema } from "./sections/about";
import { faqSchema } from "./sections/faq";
import { productDetailsSchema } from "./sections/product-details";
import { productGallerySchema } from "./sections/product-gallery";
import { productBuyboxSchema } from "./sections/product-buybox";
import { productBuybarSchema } from "./sections/product-buybar";
import { productPoliciesSchema } from "./sections/product-policies";
import { productRelatedSchema } from "./sections/product-related";
import { productRecentlyViewedSchema } from "./sections/product-recently-viewed";
import { productReviewsSchema } from "./sections/product-reviews";
import { collectionHeaderSchema } from "./sections/collection-header";
import { collectionProductsSchema } from "./sections/collection-products";
import { storiesSchema } from "./sections/stories";
import { topSellerSchema } from "./sections/top-seller";
import { valueStripSchema } from "./sections/value-strip";
import { featuresSchema } from "./sections/features";
import { testimonialsSchema } from "./sections/testimonials";
import { videoSchema } from "./sections/video";
import { instagramSchema } from "./sections/instagram";
import { newsletterSchema } from "./sections/newsletter";
import { beforeAfterSchema } from "./sections/before-after";
import { shopTheLookSchema } from "./sections/shop-the-look";
import { countdownSchema } from "./sections/countdown";
import { highlightHeadlineSchema } from "./sections/highlight-headline";
import { marqueeBandSchema } from "./sections/marquee-band";
import { scrollRevealSchema } from "./sections/scroll-reveal";
import { searchPromptSchema } from "./sections/search-prompt";
/**
 * Section types that do NOT receive the shared animation controls. These are
 * either page chrome rendered outside the animatable section wrapper (header,
 * footer — owned by `BuyerShell`) or structural page headers that aren't
 * rendered through the home page's `SectionReveal` wrapper. Injecting the
 * animation group into these would surface controls that do nothing.
 */
const NON_ANIMATED_SECTION_TYPES = new Set([
    "header",
    "nav",
    "announcement-bar",
    "footer",
    "product-details",
    "product-gallery",
    "product-buybox",
    "product-buybar",
    "product-policies",
    "product-related",
    "product-recently-viewed",
    "product-reviews",
    "collection-header",
    "collection-products",
]);
/**
 * Append the shared `animationFields()` to every animatable section's settings.
 * Keeping this at registry-assembly time means current AND future sections get
 * the animation group without each schema file opting in, and the seller editor
 * + buyer renderer + validator all read the same injected fields. Sections that
 * already declare an `animation` field, or are in `NON_ANIMATED_SECTION_TYPES`,
 * are left untouched.
 */
function withAnimationFields(schema) {
    if (NON_ANIMATED_SECTION_TYPES.has(schema.type))
        return schema;
    if (schema.settings.some((f) => f.id === "animation"))
        return schema;
    return { ...schema, settings: [...schema.settings, ...animationFields()] };
}
/**
 * Append the shared responsive layout controls (section spacing + heading
 * size) to every content section, same rationale as `withAnimationFields`.
 * Excludes page chrome / off-home structural sections that don't render
 * through the home section wrapper that consumes these vars. Skips a section
 * that already declares its own `sectionSpacing` field.
 */
function withResponsiveLayoutFields(schema) {
    if (NON_ANIMATED_SECTION_TYPES.has(schema.type))
        return schema;
    if (schema.settings.some((f) => f.id === "sectionSpacing"))
        return schema;
    return {
        ...schema,
        settings: [...schema.settings, ...responsiveLayoutFields()],
    };
}
const rawSections = [
    headerSchema,
    navSchema,
    announcementBarSchema,
    heroSchema,
    collectionsSchema,
    categorySectionsSchema,
    categoryTabsSchema,
    allProductsSchema,
    footerSchema,
    bannerSchema,
    aboutSchema,
    faqSchema,
    productDetailsSchema,
    productGallerySchema,
    productBuyboxSchema,
    productBuybarSchema,
    productPoliciesSchema,
    productRelatedSchema,
    productRecentlyViewedSchema,
    productReviewsSchema,
    collectionHeaderSchema,
    collectionProductsSchema,
    storiesSchema,
    topSellerSchema,
    valueStripSchema,
    featuresSchema,
    testimonialsSchema,
    videoSchema,
    instagramSchema,
    newsletterSchema,
    countdownSchema,
    beforeAfterSchema,
    shopTheLookSchema,
    highlightHeadlineSchema,
    marqueeBandSchema,
    scrollRevealSchema,
    searchPromptSchema,
];
const sections = Object.fromEntries(rawSections.map((schema) => [
    schema.type,
    withResponsiveLayoutFields(withAnimationFields(schema)),
]));
/** Flatten all blocks declared inside section schemas into a top-level lookup. */
function buildBlockMap() {
    const blocks = {};
    for (const section of Object.values(sections)) {
        if (!section.blocks)
            continue;
        for (const block of section.blocks) {
            blocks[block.type] = block;
        }
    }
    return blocks;
}
export const sectionRegistry = {
    sections,
    blocks: buildBlockMap(),
    theme: themeSchema,
};
/** Returns the section schema for a given section type, or `undefined`. */
export function getSectionSchema(type) {
    return sectionRegistry.sections[type];
}
/** Returns the block schema for a given block type, or `undefined`. */
export function getBlockSchema(type) {
    return sectionRegistry.blocks[type];
}
/** Section types that are locked (cannot be removed/hidden/reordered out of slot). */
export function isLockedSectionType(type) {
    return sectionRegistry.sections[type]?.locked === true;
}
/** Section types that cannot be hidden via the editor (must remain visible). */
export function isCriticalForSeo(type) {
    return sectionRegistry.sections[type]?.criticalForSeo === true;
}
/**
 * Whether a section type is allowed on the given template. A schema's
 * `availableOnTemplates` is the source of truth; if it's missing/empty the
 * section is considered universal (allowed everywhere).
 */
export function isSectionAvailableOnTemplate(sectionType, template) {
    const schema = sectionRegistry.sections[sectionType];
    if (!schema)
        return false;
    const allowed = schema.availableOnTemplates;
    if (!allowed || allowed.length === 0)
        return true;
    return allowed.includes(template);
}
/** All locked section types that MUST appear (exactly once) on a given
 *  template. Computed from each schema's `locked` + `availableOnTemplates`
 *  fields. Used by the validator to enforce structural minima. */
export function requiredLockedSectionTypes(template) {
    const result = [];
    for (const [type, schema] of Object.entries(sectionRegistry.sections)) {
        if (!schema.locked)
            continue;
        // Deprecated sections (e.g. the split-up `product-details`) stay registered
        // for back-compat but are never required — their replacements are.
        if (schema.deprecated)
            continue;
        if (!isSectionAvailableOnTemplate(type, template))
            continue;
        result.push(type);
    }
    return result;
}
//# sourceMappingURL=registry.js.map